/* Copyright (c) 2020-present CloudIO Inc */

import { atom, useStateXAtomValue } from '@cloudio-saas/statex';
import { Box, Paper, Popper as MUIPopover } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { VirtualElement } from '@popperjs/core';
import clsx from 'clsx';
import { usePageRefObject } from 'common/Context';
import { BaseProps, Popover } from 'componentregistry';
import type { VirtualElementType } from 'components/BingMap/BingMapMain';
import zIndex from 'constants/ZIndex';
import { useDeveloperMode } from 'designer/atoms';
import { IORef } from 'designer/useIORef';
import useHandleClickOutside from 'hooks/useHandleClickOutside';
import useWindowSize from 'hooks/useWindowSize';
import { useParentItemId } from 'platform/PlatformHooks';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTheme } from 'theme/Theme';
import { backgroundColorFn } from 'theme/useThemeColor';
import useTrigger from 'triggers/useTrigger';

const useStyles = makeStyles(() => ({
  popper: {
    padding: '0',
    visibility: 'visible',
    '&[data-popper-placement^="bottom"] > $arrow': {
      top: 0,
      left: 0,
      marginTop: '-8px',
      width: '24px',
      height: '9px',
      '&::before': {
        borderWidth: '0 8px 8px 8px',
      },
    },
    '&[data-popper-placement^="top"] > $arrow': {
      bottom: 0,
      left: 0,
      marginBottom: '-8px',
      width: '24px',
      height: '9px',
      '&::before': {
        borderWidth: '8px 8px 0 8px',
      },
    },
    '&[data-popper-placement^="right"] > $arrow': {
      left: 0,
      top: 0,
      marginLeft: '-8px',
      height: '24px',
      width: '9px',
      '&::before': {
        borderWidth: '8px 8px 8px 0',
      },
    },
    '&[data-popper-placement^="left"] > $arrow': {
      right: 0,
      top: 0,
      marginRight: '-8px',
      height: '24px',
      width: '9px',
      '&::before': {
        borderWidth: '8px 0 8px 8px',
      },
    },
  },
  arrow: {
    position: 'absolute',
    fontSize: 7,
    zIndex: 1,
    width: '24px',
    height: '24px',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    },
  },
  popperHidden: {
    visibility: 'hidden',
  },
}));

export interface PopoverProps
  extends Required<Popover.Popover>,
    BaseProps<'Popover'> {
  anchorRef: IORef<HTMLOrSVGElement>;
  onMouseEnter?:
    | ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void)
    | undefined;
  onMouseLeave?:
    | ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void)
    | undefined;
  onClose: (e: MouseEvent | TouchEvent, reason?: 'OutSideClick') => void;
  anchorId?: string;
}

export const manualPopoverOpenAtom = atom<{
  currentAnchorId: string | undefined;
  open: boolean;
}>({
  path: ['transient', ':appUid', ':pageId', ':popoverId', 'manualPopoverOpen'],
  defaultValue: {
    open: false,
    currentAnchorId: undefined,
  },
});

function IOPopover(props: PopoverProps, ref: React.Ref<HTMLOrSVGElement>) {
  const {
    anchorRef,
    appUid,
    roleUid,
    borderRadius,
    children,
    onClose,
    disableFlip,
    onMouseEnter,
    onMouseLeave,
    itemId,
    pageId,
    placement,
    platform,
    popoverHeight,
    popoverWidth,
    backgroundColor,
    anchorId,
  } = props;
  let { open } = props;
  const theme = useTheme();
  const bgcolor = backgroundColorFn(theme, backgroundColor);
  const classes = useStyles();
  const devMode = useDeveloperMode(appUid, pageId);
  const [el, setEl] = useState<VirtualElementType | null>(null);
  const manualPopoverOpen = useStateXAtomValue(manualPopoverOpenAtom, {
    params: { appUid, pageId, popoverId: itemId },
  });

  // Added 'currentAnchorId === anchorId' condition to fix issue #835;
  open = manualPopoverOpen.open
    ? open && manualPopoverOpen.currentAnchorId === anchorId
    : open;
  anchorRef?.subscribe((ele) => {
    if (el !== ele) {
      setEl(ele as unknown as VirtualElementType);
    }
  });

  const parentItemId = useParentItemId(appUid, pageId, itemId);
  const parentItem = parentItemId
    ? platform.getItem(appUid, pageId, parentItemId)
    : undefined;

  const [onPopoverClose] = useTrigger(
    {
      appUid,
      roleUid,
      platform,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnPopoverClose',
  );
  const [onPopoverOpen] = useTrigger(
    {
      appUid,
      roleUid,
      platform,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnPopoverOpen',
  );

  const { pageHeight, pageWidth } = usePageRefObject();
  const paperRef = useRef<HTMLDivElement>(null);
  useHandleClickOutside(paperRef, onClose, open);
  const [ww, wh] = useWindowSize();

  useEffect(() => {
    if (devMode || !open) return;
    onPopoverOpen?.();
    return () => {
      onPopoverClose?.();
    };
  }, [devMode, onPopoverClose, onPopoverOpen, open]);

  let ele: VirtualElementType | null = el;
  if (!(open && el)) {
    if (open && devMode) {
      const vel: VirtualElementType = {
        getBoundingClientRect: () => ({
          width: 0,
          height: 0,
          top: wh / 2,
          right: 0,
          bottom: 0,
          left: ww / 2,
          x: 0,
          y: 0,
        }),
      };
      ele = vel;
    } else {
      return null;
    }
  }
  let p = placement;
  // @ts-ignore flip
  if (el?.getBoundingClientRect()?.flip) {
    // set in BingMapMain
    p = 'bottom';
  }
  // console.log({ placement, p, disableFlip });
  const boundary = 'document';

  return (
    <MUIPopover
      data-metadata="cloudio"
      className={clsx('popover', classes.popper)}
      sx={{
        zIndex:
          parentItem?.type === 'Calendar' ? zIndex.Model + 899 : zIndex.Model,
      }}
      open={open}
      anchorEl={ele as unknown as VirtualElement}
      placement={p}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      modifiers={[
        {
          name: 'flip',
          enabled: !disableFlip,
          options: {
            altBoundary: true,
            rootBoundary: boundary,
            padding: 0,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            mainAxis: true,
            altBoundary: true,
            tether: true,
            boundary,
            rootBoundary: boundary,
            padding: 0,
          },
        },
        {
          name: 'arrow',
          enabled: true,
          options: {
            padding: 0,
          },
        },
        {
          name: 'offset',
          enabled: true,
          options: { offset: [0, 9] },
        },
      ]}
    >
      <Box
        sx={{
          '[data-popper-placement^="top"] &::before': {
            borderColor: `${bgcolor} transparent transparent transparent`,
          },
          '[data-popper-placement^="bottom"] &::before': {
            borderColor: `transparent transparent ${bgcolor} transparent`,
          },
          '[data-popper-placement^="right"] &::before': {
            borderColor: `transparent ${bgcolor} transparent transparent`,
          },
          '[data-popper-placement^="left"] &::before': {
            borderColor: `transparent transparent transparent ${bgcolor}`,
          },
        }}
        data-popper-arrow
        className={clsx('arrow', classes.arrow)}
      />
      <Paper
        ref={paperRef}
        elevation={3}
        sx={{
          borderRadius: borderRadius,
          overflow: 'auto',
          maxHeight: pageHeight - 40,
          bgcolor,
        }}
      >
        {React.Children.map<ReactNode, ReactNode>(children, (child) => {
          if (React.isValidElement<BaseProps<never>>(child)) {
            return React.cloneElement(child, {
              width: Math.min(popoverWidth, pageWidth),
              height: Math.min(popoverHeight, pageHeight - 40),
            });
          }
          return child;
        })}
      </Paper>
    </MUIPopover>
  );
}

export default React.forwardRef(IOPopover);
